/**
 * Template Build Status Hook
 *
 * Polling hook for template build status.
 * Automatically polls until build is ready for review, completed, or failed.
 * Includes timeout protection (5 minutes max).
 */

import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  isReadyForReview,
  toBuildStatus,
  useGetTemplateBuildStatusQuery,
} from '../store/api';
import {
  setBuildStatus,
  setCreatedTemplateId,
  setDocumentSummary,
  setDocumentTitle,
  setDraftTemplate,
  setError,
  setNeedsStatusRefetch,
  setProgressMessage,
} from '../store/chatSlice';
import {
  selectBuildStatus,
  selectDocumentSummary,
  selectDocumentTitle,
  selectDraftTemplate,
  selectNeedsStatusRefetch,
  selectProgressMessage,
} from '../store/selectors';
import { toDraftTemplate } from '../types';

// Polling interval in milliseconds
const POLL_INTERVAL = 2000;
// Maximum polling duration (5 minutes)
const MAX_POLLING_DURATION_MS = 5 * 60 * 1000;

/**
 * Hook for polling template build status
 *
 * @param taskId - The template build task ID
 * @returns Build status, progress message, draft template, and polling state
 */
export const useTemplateBuildStatus = (taskId: string | null) => {
  const dispatch = useAppDispatch();
  const buildStatus = useAppSelector(selectBuildStatus);
  const progressMessage = useAppSelector(selectProgressMessage);
  const documentTitle = useAppSelector(selectDocumentTitle);
  const documentSummary = useAppSelector(selectDocumentSummary);
  const draftTemplate = useAppSelector(selectDraftTemplate);
  const needsStatusRefetch = useAppSelector(selectNeedsStatusRefetch);

  // Track when polling started for timeout
  const pollingStartTimeRef = useRef<number | null>(null);
  const [hasTimedOut, setHasTimedOut] = useState(false);

  // Reset timeout tracking when taskId changes
  useEffect(() => {
    if (taskId) {
      pollingStartTimeRef.current = Date.now();
      setHasTimedOut(false);
    } else {
      pollingStartTimeRef.current = null;
      setHasTimedOut(false);
    }
  }, [taskId]);

  // Check if we've exceeded the timeout
  useEffect(() => {
    // Don't set up timeout if already timed out, no taskId, or in terminal state
    if (!pollingStartTimeRef.current || hasTimedOut || !taskId) return;
    if (buildStatus === 'completed' || buildStatus === 'failed' || buildStatus === 'ready_for_review') {
      return;
    }

    const checkTimeout = () => {
      // Double-check conditions before setting timeout
      if (!pollingStartTimeRef.current || hasTimedOut) return;
      
      const elapsed = Date.now() - pollingStartTimeRef.current;
      if (elapsed >= MAX_POLLING_DURATION_MS) {
        setHasTimedOut(true);
        dispatch(setError('Template build is taking longer than expected. Please try again or contact support.'));
        dispatch(setBuildStatus('failed'));
        // Clear the polling start time to prevent further checks
        pollingStartTimeRef.current = null;
      }
    };

    const interval = setInterval(checkTimeout, 1000); // Check every second
    return () => clearInterval(interval);
  }, [dispatch, hasTimedOut, taskId, buildStatus]);

  // Determine if we should be polling
  // Stop polling if:
  // - No taskId
  // - Status is completed, failed, or ready_for_review (terminal states)
  // - We've timed out
  // - Status is idle or uploading (not yet processing)
  const shouldPoll =
    taskId !== null &&
    !hasTimedOut &&
    buildStatus !== 'completed' &&
    buildStatus !== 'failed' &&
    buildStatus !== 'ready_for_review' &&
    buildStatus !== 'idle' &&
    buildStatus !== 'uploading' &&
    (buildStatus === 'processing' || buildStatus === 'structure_ready');

  // Query with polling
  // Skip query if no taskId, timed out, or in terminal state
  const shouldSkipQuery = !taskId || hasTimedOut || buildStatus === 'completed' || buildStatus === 'failed' || buildStatus === 'ready_for_review';
  
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetTemplateBuildStatusQuery(
    { taskId: taskId || '' },
    {
      skip: shouldSkipQuery,
      pollingInterval: shouldPoll ? POLL_INTERVAL : 0,
    },
  );

  // Sync API response to Redux state
  useEffect(() => {
    if (!data) return;
    
    // Don't process data if we've already timed out
    if (hasTimedOut) return;

    // Update build status
    const newStatus = toBuildStatus(data.status);
    if (newStatus !== buildStatus) {
      dispatch(setBuildStatus(newStatus));
      
      // Stop polling if we've reached a terminal state
      if (newStatus === 'completed' || newStatus === 'failed' || newStatus === 'ready_for_review') {
        pollingStartTimeRef.current = null;
        setHasTimedOut(false);
      }
    }

    // Handle API errors
    if (data.error) {
      dispatch(setError(data.error));
      dispatch(setBuildStatus('failed'));
      pollingStartTimeRef.current = null;
      setHasTimedOut(false);
    }

    // Update progress message
    if (data.progress_message !== progressMessage) {
      dispatch(setProgressMessage(data.progress_message ?? null));
    }

    // Update document metadata (for PDFs, available early)
    // Type assertion needed because generated types may not have these fields yet
    const apiData = data as typeof data & {
      document_title?: string | null;
      document_summary?: string | null;
    };

    if (apiData.document_title !== undefined && apiData.document_title !== documentTitle) {
      dispatch(setDocumentTitle(apiData.document_title ?? null));
    }

    if (apiData.document_summary !== undefined && apiData.document_summary !== documentSummary) {
      dispatch(setDocumentSummary(apiData.document_summary ?? null));
    }

    // Update draft template if present
    if (data.draft_template) {
      const template = toDraftTemplate(data.draft_template);
      dispatch(setDraftTemplate(template));
    }

    // Set created template ID if build is completed
    if (data.created_template_id) {
      dispatch(setCreatedTemplateId(data.created_template_id));
    }
  }, [data, dispatch, buildStatus, progressMessage, documentTitle, documentSummary, hasTimedOut]);

  // Handle query errors
  useEffect(() => {
    if (error && !hasTimedOut) {
      dispatch(setError('Failed to check template build status. Please try again.'));
      dispatch(setBuildStatus('failed'));
      pollingStartTimeRef.current = null;
      setHasTimedOut(false);
    }
  }, [error, dispatch, hasTimedOut]);

  // Trigger refetch after chat completes (when SSE RUN_FINISHED sets the flag)
  useEffect(() => {
    if (needsStatusRefetch && taskId && !shouldSkipQuery) {
      console.debug('📡 [Status] Triggering refetch after chat completion');
      void refetch();
      dispatch(setNeedsStatusRefetch(false));
    }
  }, [needsStatusRefetch, taskId, shouldSkipQuery, refetch, dispatch]);

  return {
    buildStatus,
    progressMessage,
    documentTitle,
    documentSummary,
    draftTemplate,
    /** True on initial load only (no cached data yet) */
    isLoading,
    /** True during any fetch (initial or polling) */
    isFetching,
    /** True when polling is active (status is still processing) - stable during poll cycles */
    isPolling: shouldPoll,
    error,
    refetch,
    /** Whether the build is ready for chat interaction */
    isReadyForChat: taskId !== null && isReadyForReview(buildStatus),
  };
};

